/**
 * Trunnion AI - Application Router
 * Defines all routes with auth guards and layout wrappers.
 */

import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { ProtectedRoute } from './ProtectedRoute';
import { ROUTES } from '@/config/routes';

// Lazy-loaded pages
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import WorkflowListPage from '@/pages/WorkflowListPage';
import WorkflowDesignerPage from '@/pages/WorkflowDesignerPage';
import ExecutionListPage from '@/pages/ExecutionListPage';
import ExecutionPage from '@/pages/ExecutionPage';
import AuditPage from '@/pages/AuditPage';
import AgentListPage from '@/pages/AgentListPage';
import AgentDetailPage from '@/pages/AgentDetailPage';
import AdminPage from '@/pages/AdminPage';
import NotFoundPage from '@/pages/NotFoundPage';
import AgentChatPage from '@/pages/AgentChatPage';
import SettingsPage from '@/pages/SettingsPage';
import TemplateGalleryPage from '@/pages/TemplateGalleryPage';
import SearchPage from '@/pages/SearchPage';
import MissionBoardPage from '@/pages/MissionBoardPage';
import ExecutionComparePage from '@/pages/ExecutionComparePage';

// Admin sub-pages
import { PolicyEditor } from '@/components/admin/PolicyEditor';
import { ToolRegistryManager } from '@/components/admin/ToolRegistryManager';
import { LLMProviderConfig } from '@/components/admin/LLMProviderConfig';
import { UserManagement } from '@/components/admin/UserManagement';
import { PolicySimulator } from '@/components/admin/PolicySimulator';

export const router = createBrowserRouter([
  {
    path: ROUTES.LOGIN,
    element: <LoginPage />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to={ROUTES.DASHBOARD} replace />,
      },
      {
        path: 'dashboard',
        element: <DashboardPage />,
      },
      {
        path: 'workflows',
        element: <WorkflowListPage />,
      },
      {
        path: 'workflows/new',
        element: <WorkflowDesignerPage />,
      },
      {
        path: 'workflows/:id/edit',
        element: <WorkflowDesignerPage />,
      },
      {
        path: 'executions',
        element: <ExecutionListPage />,
      },
      {
        path: 'executions/compare',
        element: <ExecutionComparePage />,
      },
      {
        path: 'executions/:id',
        element: <ExecutionPage />,
      },
      {
        path: 'agents',
        element: <AgentListPage />,
      },
      {
        path: 'agents/new',
        element: <AgentDetailPage />,
      },
      {
        path: 'agents/:id',
        element: <AgentDetailPage />,
      },
      {
        path: 'agents/:id/chat',
        element: <AgentChatPage />,
      },
      {
        path: 'audit',
        element: <AuditPage />,
      },
      {
        path: 'templates',
        element: <TemplateGalleryPage />,
      },
      {
        path: 'missions',
        element: <MissionBoardPage />,
      },
      {
        path: 'settings',
        element: <SettingsPage />,
      },
      {
        path: 'search',
        element: <SearchPage />,
      },
      {
        path: 'admin',
        element: <AdminPage />,
        children: [
          {
            path: 'policies',
            element: <PolicyEditor />,
          },
          {
            path: 'tools',
            element: <ToolRegistryManager />,
          },
          {
            path: 'llm',
            element: <LLMProviderConfig />,
          },
          {
            path: 'users',
            element: <UserManagement />,
          },
          {
            path: 'simulator',
            element: <PolicySimulator />,
          },
        ],
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);
